import { useEffect, useState } from 'react';
import { Clock, Download, Upload, Zap } from 'lucide-react';

export interface ProgressInfo {
  bytesProcessed: number;
  totalBytes: number;
  percentage: number;
  currentPart?: number;
  totalParts?: number;
  segmentsDownloaded?: number;
  totalSegments?: number;
  currentSegment?: string;
  phase?: string;
  status: string;
  lastUpdated: string;
  startedAt?: string;
  error?: string;
  failedAt?: string;
}

interface ProgressBarProps {
  uploadId: string;
  status: string;
  progress: number;
  progressInfo?: ProgressInfo | null;
  className?: string;
}

export default function ProgressBar({ 
  status, 
  progress, 
  progressInfo, 
  className = '' 
}: ProgressBarProps) {
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    if (status !== 'processing' || !progressInfo?.startedAt) {
      return;
    }

    const interval = setInterval(() => {
      const startTime = new Date(progressInfo.startedAt!).getTime();
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setTimeElapsed(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [status, progressInfo?.startedAt]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const calculateSpeed = () => {
    if (!progressInfo?.startedAt || !progressInfo.bytesProcessed || timeElapsed === 0) {
      return '0 B/s';
    }
    const bytesPerSecond = progressInfo.bytesProcessed / timeElapsed;
    return formatBytes(bytesPerSecond) + '/s';
  };

  const calculateETA = () => {
    if (!progressInfo?.startedAt || timeElapsed === 0) {
      return 'Calculating...';
    }
    
    // Use segment-based ETA if available (more accurate for VOD processing)
    if (progressInfo.segmentsDownloaded && progressInfo.totalSegments && progressInfo.segmentsDownloaded > 0) {
      const segmentsPerSecond = progressInfo.segmentsDownloaded / timeElapsed;
      const remainingSegments = progressInfo.totalSegments - progressInfo.segmentsDownloaded;
      const etaSeconds = Math.floor(remainingSegments / segmentsPerSecond);
      
      if (etaSeconds < 60) return `${etaSeconds}s remaining`;
      if (etaSeconds < 3600) return `${Math.floor(etaSeconds / 60)}m remaining`;
      return `${Math.floor(etaSeconds / 3600)}h ${Math.floor((etaSeconds % 3600) / 60)}m remaining`;
    }
    
    // Fallback to byte-based ETA
    if (!progressInfo.bytesProcessed) {
      return 'Calculating...';
    }
    
    const bytesPerSecond = progressInfo.bytesProcessed / timeElapsed;
    const remainingBytes = progressInfo.totalBytes - progressInfo.bytesProcessed;
    const etaSeconds = Math.floor(remainingBytes / bytesPerSecond);
    
    if (etaSeconds < 60) return `${etaSeconds}s remaining`;
    if (etaSeconds < 3600) return `${Math.floor(etaSeconds / 60)}m remaining`;
    return `${Math.floor(etaSeconds / 3600)}h ${Math.floor((etaSeconds % 3600) / 60)}m remaining`;
  };

  const getPhaseIcon = () => {
    switch (progressInfo?.phase) {
      case 'downloading':
        return <Download className="h-3 w-3" />;
      case 'uploading':
        return <Upload className="h-3 w-3" />;
      case 'processing':
        return <Zap className="h-3 w-3" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const hasError = status === 'failed' || progressInfo?.error;
  const percentage = Math.min(Math.max(progressInfo?.percentage || progress, 0), 100);

  // Simple progress bar for non-processing uploads
  if (status !== 'processing' || !progressInfo) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              status === 'completed' ? 'bg-green-500' :
              status === 'failed' ? 'bg-red-500' :
              status === 'processing' ? 'bg-yellow-500' : 'bg-gray-400'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm text-gray-900">{progress}%</span>
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2 text-sm">
          {getPhaseIcon()}
          <span className="font-medium text-gray-700">
            {hasError ? 'Processing Error' : `Processing (${progressInfo.phase || 'unknown'})`}
          </span>
          {/* Segment progress indicator */}
          {progressInfo.segmentsDownloaded && progressInfo.totalSegments && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              {progressInfo.segmentsDownloaded}/{progressInfo.totalSegments} segments
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {percentage.toFixed(1)}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${
            hasError ? 'bg-red-500' : 'bg-blue-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Progress Details */}
      <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
        <div>
          {/* Prioritize segment progress */}
          {progressInfo.segmentsDownloaded && progressInfo.totalSegments && (
            <div className="flex justify-between mb-1">
              <span className="font-medium text-blue-700">Segments:</span>
              <span className="font-medium text-blue-700">
                {progressInfo.segmentsDownloaded} / {progressInfo.totalSegments}
                <span className="ml-1 text-gray-500">
                  ({((progressInfo.segmentsDownloaded / progressInfo.totalSegments) * 100).toFixed(1)}%)
                </span>
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Size:</span>
            <span>{formatBytes(progressInfo.bytesProcessed)} / {formatBytes(progressInfo.totalBytes)}</span>
          </div>
          {progressInfo.currentPart && progressInfo.totalParts && (
            <div className="flex justify-between">
              <span>Parts:</span>
              <span>{progressInfo.currentPart} / {progressInfo.totalParts}</span>
            </div>
          )}
        </div>
        <div>
          <div className="flex justify-between">
            <span>Speed:</span>
            <span>{calculateSpeed()}</span>
          </div>
          <div className="flex justify-between">
            <span>Elapsed:</span>
            <span>{formatTime(timeElapsed)}</span>
          </div>
          {!hasError && (
            <div className="flex justify-between">
              <span>ETA:</span>
              <span>{calculateETA()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {hasError && progressInfo.error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <span className="font-medium">Error: </span>
          {progressInfo.error}
        </div>
      )}

      {/* Current Segment (for debugging) */}
      {progressInfo.currentSegment && !hasError && (
        <div className="mt-2 text-xs text-gray-500 truncate">
          Current: {progressInfo.currentSegment.split('/').pop()}
        </div>
      )}
    </div>
  );
}